import {
  Button,
  Input,
  Box,
  HStack,
  RadioGroup,
  Switch,
  Text,
  Badge,
} from "@chakra-ui/react";
import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import {
  Transaction,
  transactionAdded,
  transactionUpdated,
} from "@/stores/transactions-store/transactions-reducer";
import { useDispatch } from "react-redux";
import { AppDispatch } from "@/stores/store";
import { createTransaction } from "@/lib/api-calls/transactions/create-transaction";
import { updateTransaction } from "@/lib/api-calls/transactions/update-transaction";
import { i18n } from "@/lib/utils/i18n";
import { CategoryCombobox } from "./category-combobox";
import { ReceiptUploader } from "@/components/shared/receipt-uploader";
import { ReceiptList } from "@/components/shared/receipt-list";
import { PropertySelectMenu } from "./property-select-menu";
import { useSession } from "next-auth/react";
import { Flyout } from "@/components/shared/flyout";
import { toaster } from "./atoms/toaster";
import { useCategorizationRuleModal } from "@/components/settings/categorization-rules/use-categorization-rule-modal";
import { CategorizationRuleModal } from "@/components/settings/categorization-rules/categorization-rule-modal";
import React from "react";

interface TransactionFormData {
  name: string;
  amount: string;
  date: string;
  source: "user_created";
  externalId: string;
  category: string;
  propertyId: string | undefined;
  organizationId: string;
}

export function TransactionFlyout({
  isOpen,
  setIsOpen,
  transactionId,
  transactions,
  closeFlyout,
}: {
  isOpen?: boolean;
  setIsOpen: (open: boolean) => void;
  transactionId?: string;
  transactions?: Transaction[];
  closeFlyout?: () => void;
}) {
  const dispatch = useDispatch<AppDispatch>();
  const { data: session } = useSession();
  const { openCreateModal, modalProps } = useCategorizationRuleModal();

  const emptyFormData = useMemo(
    (): TransactionFormData => ({
      name: "",
      amount: "",
      date: "",
      source: "user_created",
      externalId: "",
      category: "",
      propertyId: undefined,
      organizationId: session?.organizationId || "",
    }),
    [session?.organizationId],
  );

  const transaction = transactions?.find((t) => t.id === transactionId);

  const getDefaultFormData = useCallback(() => {
    if (!transactionId) return emptyFormData;

    const currentTransaction = transactions?.find(
      (t) => t.id === transactionId,
    );
    if (!currentTransaction) return emptyFormData;

    return {
      name: currentTransaction.name ?? "",
      amount: currentTransaction.amount?.toString() ?? "",
      date: new Date(currentTransaction.date).toISOString().split("T")[0],
      source: currentTransaction.source ?? "user_created",
      externalId: currentTransaction.externalId,
      category: currentTransaction.category ?? "",
      propertyId: currentTransaction.propertyId,
      organizationId: session?.organizationId || "",
    };
  }, [transactionId, transactions, emptyFormData, session?.organizationId]);

  const [formData, setFormData] = useState(getDefaultFormData());
  const [loading, setLoading] = useState(false);
  const [transactionType, setTransactionType] = useState<"in" | "out">(
    Number(formData.amount) >= 0 ? "in" : "out",
  );
  const [originalCategory, setOriginalCategory] = useState<string>("");
  const [isIgnored, setIsIgnored] = useState(false);

  const handleTransactionTypeChange = (details: { value: string | null }) => {
    if (details.value) {
      setTransactionType(details.value as "in" | "out");
      const currentAmount = Number(formData.amount);
      setFormData((prev) => ({
        ...prev,
        amount:
          details.value === "out"
            ? (-Math.abs(currentAmount)).toString()
            : Math.abs(currentAmount).toString(),
      }));
    }
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const numericValue = Number(value);
    setFormData((prev) => ({
      ...prev,
      amount:
        transactionType === "out"
          ? (-Math.abs(numericValue)).toString()
          : Math.abs(numericValue).toString(),
    }));
  };

  useEffect(() => {
    if (transactionId && transaction) {
      const defaultData = getDefaultFormData();
      setFormData(defaultData);
      setOriginalCategory(defaultData.category);
      setIsIgnored(transaction.isIgnored || false);
    } else if (!transactionId) {
      setFormData(emptyFormData);
      setOriginalCategory("");
      setIsIgnored(false);
    }
  }, [transactionId, transaction, getDefaultFormData, emptyFormData]);

  useEffect(() => {
    setLoading(false);
  }, [transactionId, isOpen]);

  useEffect(() => {
    const amt = Number(formData.amount);
    setTransactionType(amt >= 0 ? "in" : "out");
  }, [formData.amount]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleCancel = () => {
    closeFlyout?.();
  };

  const handleClose = () => {
    closeFlyout?.();
  };

  const handleIgnoreToggle = async () => {
    if (!transactionId) return;

    try {
      const response = await fetch(
        `/api/transactions/${transactionId}/ignore`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (response.ok) {
        const updatedTransaction = await response.json();
        dispatch(
          transactionUpdated({
            id: updatedTransaction.id,
            changes: { isIgnored: updatedTransaction.isIgnored },
          }),
        );
        setIsIgnored(updatedTransaction.isIgnored);

        toaster.create({
          title: "Success",
          description: `Transaction ${updatedTransaction.isIgnored ? "ignored" : "unignored"} successfully!`,
          type: "success",
          duration: 3000,
          closable: true,
        });
      } else {
        throw new Error("Failed to toggle ignore status");
      }
    } catch (error) {
      toaster.create({
        title: "Error",
        description: "Failed to update ignore status",
        type: "error",
        duration: 5000,
        closable: true,
      });
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      let responseTransaction;
      if (transactionId) {
        responseTransaction = await updateTransaction({
          id: transactionId,
          ...formData,
        });
        dispatch(
          transactionUpdated({
            id: responseTransaction.id,
            changes: responseTransaction,
          }),
        );

        const categoryChanged =
          originalCategory !== formData.category && formData.category;
        if (categoryChanged) {
          setTimeout(() => {
            toaster.create({
              title: "Category Updated",
              description:
                "Want to automate similar categorizations? Create a categorization rule!",
              type: "info",
              duration: 5000,
              closable: true,
              action: {
                label: "Create Rule",
                onClick: () => {
                  openCreateModal(
                    formData.name,
                    formData.category,
                    formData.name,
                  );
                },
              },
            });
          }, 1500);
        }

        setTimeout(() => {
          toaster.create({
            title: "Success",
            description: "Transaction updated successfully!",
            type: "success",
            duration: 3000,
            closable: true,
          });
        }, 100);
      } else {
        responseTransaction = await createTransaction(formData);
        dispatch(transactionAdded(responseTransaction));
        setTimeout(() => {
          toaster.create({
            title: "Success",
            description: "Transaction created successfully!",
            type: "success",
            duration: 3000,
            closable: true,
          });
        }, 100);
      }
      setFormData(emptyFormData);
    } catch (error: unknown) {
      if (error instanceof Error) {
        toaster.create({
          title: "Error",
          description: `Error: ${error.message}`,
          type: "error",
          duration: 5000,
          closable: true,
        });
      } else {
        toaster.create({
          title: "Error",
          description: "An unexpected error occurred.",
          type: "error",
          duration: 5000,
          closable: true,
        });
      }
    } finally {
      setLoading(false);
      closeFlyout?.();
    }
  };

  return (
    <>
      <Flyout
        isOpen={!!isOpen}
        setIsOpen={setIsOpen}
        title={
          transactionId ? i18n.t("Edit Transaction") : i18n.t("Add Transaction")
        }
        maxWidth='600px'
        onClose={handleClose}
        footer={null}
      >
        <form onSubmit={handleSubmit}>
          <Box p={6}>
            {/* Basic Information Section */}
            <Box mb={8}>
              <Text fontSize='lg' fontWeight='600' mb={4} color='gray.800'>
                Transaction Details
              </Text>
              <Box>
                <Box mb={4}>
                  <Text fontSize='sm' fontWeight='500' mb={2} color='gray.700'>
                    Name *
                  </Text>
                  <Input
                    type='text'
                    placeholder='Enter transaction name'
                    value={formData.name}
                    onChange={handleChange}
                    required
                    name='name'
                    size='md'
                    borderRadius='md'
                    borderColor='gray.300'
                    _focus={{
                      borderColor: "blue.500",
                      boxShadow: "0 0 0 1px #3182ce",
                    }}
                  />
                </Box>

                <HStack gap={4} mb={4}>
                  <Box flex={1}>
                    <Text
                      fontSize='sm'
                      fontWeight='500'
                      mb={2}
                      color='gray.700'
                    >
                      Date *
                    </Text>
                    <Input
                      name='date'
                      type='date'
                      value={formData.date}
                      onChange={handleChange}
                      required
                      size='md'
                      borderRadius='md'
                      borderColor='gray.300'
                      _focus={{
                        borderColor: "blue.500",
                        boxShadow: "0 0 0 1px #3182ce",
                      }}
                    />
                  </Box>
                  <Box flex={1}>
                    <Text
                      fontSize='sm'
                      fontWeight='500'
                      mb={2}
                      color='gray.700'
                    >
                      Amount *
                    </Text>
                    <Input
                      name='amount'
                      type='number'
                      placeholder='0.00'
                      value={Math.abs(Number(formData.amount))}
                      onChange={handleAmountChange}
                      required
                      size='md'
                      borderRadius='md'
                      borderColor='gray.300'
                      _focus={{
                        borderColor: "blue.500",
                        boxShadow: "0 0 0 1px #3182ce",
                      }}
                    />
                  </Box>
                </HStack>

                <Box mb={4}>
                  <Text fontSize='sm' fontWeight='500' mb={2} color='gray.700'>
                    Transaction Type
                  </Text>
                  <RadioGroup.Root
                    value={transactionType}
                    onValueChange={handleTransactionTypeChange}
                  >
                    <HStack gap={4}>
                      <RadioGroup.Item value='in'>
                        <RadioGroup.ItemHiddenInput />
                        <RadioGroup.ItemIndicator />
                        <RadioGroup.ItemText>Money In</RadioGroup.ItemText>
                      </RadioGroup.Item>
                      <RadioGroup.Item value='out'>
                        <RadioGroup.ItemHiddenInput />
                        <RadioGroup.ItemIndicator />
                        <RadioGroup.ItemText>Money Out</RadioGroup.ItemText>
                      </RadioGroup.Item>
                    </HStack>
                  </RadioGroup.Root>
                </Box>
              </Box>
            </Box>

            {/* Categorization Section */}
            <Box mb={8}>
              <Text fontSize='lg' fontWeight='600' mb={4} color='gray.800'>
                Categorization
              </Text>
              <Box>
                <Box mb={4}>
                  <Text fontSize='sm' fontWeight='500' mb={2} color='gray.700'>
                    Category
                  </Text>
                  <CategoryCombobox
                    value={formData.category || ""}
                    onChange={(value) =>
                      setFormData((prev) => ({
                        ...prev,
                        category: value as string,
                      }))
                    }
                    multiple={false}
                    width='100%'
                  />
                </Box>

                <Box mb={4}>
                  <Text fontSize='sm' fontWeight='500' mb={2} color='gray.700'>
                    Property
                  </Text>
                  <PropertySelectMenu
                    value={formData.propertyId ?? undefined}
                    onValueChange={(value: string | undefined) =>
                      setFormData((prev) => ({ ...prev, propertyId: value }))
                    }
                  />
                </Box>
              </Box>
            </Box>

            {/* Transaction Status Section (only for existing transactions) */}
            {transactionId && (
              <Box mb={8}>
                <Text fontSize='lg' fontWeight='600' mb={4} color='gray.800'>
                  Transaction Status
                </Text>
                <Box
                  p={4}
                  borderRadius='md'
                  border='1px solid'
                  borderColor={isIgnored ? "orange.200" : "gray.200"}
                  bg={isIgnored ? "orange.50" : "gray.50"}
                >
                  <HStack justify='space-between' align='center' mb={2}>
                    <HStack gap={3}>
                      <Text fontSize='sm' fontWeight='500' color='gray.700'>
                        Ignore Transaction
                      </Text>
                      {isIgnored && (
                        <Badge colorScheme='orange' size='sm'>
                          Ignored
                        </Badge>
                      )}
                    </HStack>
                    <Switch.Root
                      checked={isIgnored}
                      onCheckedChange={handleIgnoreToggle}
                      size='md'
                    >
                      <Switch.HiddenInput />
                      <Switch.Control />
                    </Switch.Root>
                  </HStack>
                  <Text fontSize='xs' color='gray.600'>
                    Ignored transactions are excluded from all calculations and
                    reports
                  </Text>
                </Box>
              </Box>
            )}

            {/* Receipts Section */}
            <Box mb={8}>
              <Text fontSize='lg' fontWeight='600' mb={4} color='gray.800'>
                Receipts
              </Text>
              <Box>
                <Box mb={4}>
                  <Text fontSize='sm' fontWeight='500' mb={2} color='gray.700'>
                    Upload Receipt
                  </Text>
                  <ReceiptUploader transactionId={transactionId} />
                </Box>
                {transactionId && <ReceiptList transactionId={transactionId} />}
              </Box>
            </Box>

            {/* Action Buttons */}
            <HStack gap={3} justify='flex-end' pt={4}>
              <Button
                variant='outline'
                onClick={handleCancel}
                type='button'
                size='md'
                borderRadius='md'
              >
                Cancel
              </Button>
              <Button
                colorScheme='blue'
                type='submit'
                loading={loading}
                size='md'
                borderRadius='md'
                px={6}
              >
                {loading
                  ? "Saving..."
                  : transactionId
                    ? "Update Transaction"
                    : "Create Transaction"}
              </Button>
            </HStack>
          </Box>
        </form>
      </Flyout>

      <CategorizationRuleModal
        showModal={modalProps.showModal}
        setShowModal={modalProps.setShowModal}
        rule={modalProps.editingRule}
        organizationId={session?.organizationId || ""}
        onSave={() => {}}
        isEditing={modalProps.isEditing}
        transactions={transactions}
      />
    </>
  );
}
